// Input bar with slash-command autocomplete. Typing / opens the menu,
// sourced from the slash registry. Arrow keys navigate the highlight; Tab
// completes the highlighted command; Esc dismisses. ENTER ALWAYS SUBMITS
// (Shift+Enter inserts a newline) — the menu is a discovery aid, never an
// Enter trap. Matching + display use the command's `slash` (e.g. "/pipeline"),
// not the canonical tool name ("PipelineView").
//
// The dropdown is a plain div, NOT cmdk. cmdk ran a useLayoutEffect that
// called focus()/scrollIntoView on its selected item every render; over the
// sibling controlled <textarea> that synchronous focus churn raced the caret
// during fast typing — typing "/kyc" left the caret one char back. cmdk added
// nothing here anyway (filtering, the active-index highlight, and keyboard nav
// are all hand-rolled below; shouldFilter was already off). A plain list with
// onMouseDown (preventDefault keeps textarea focus) removes the race entirely.

import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import type { SlashCommand } from '../../lib/parse-slash';

// Commands that take an argument (a prospectId, query, or — for admin — an
// officerId / investorId). Choosing these from the menu fills the input +
// focuses instead of running immediately, so the founder can type the
// argument. Everything else runs on select. (Cross-role: outreach + admin
// slashes coexist here; commands absent in a given app are simply never shown.)
const ARG_REQUIRED = new Set([
  '/draft', '/search', '/show', '/qualify', '/dismiss', // outreach
  '/kyc', '/seed', '/queue',                            // admin (H.3.5)
]);

type Props = {
  value: string;
  setValue: (v: string) => void;
  onSubmit: (text: string) => void;
  disabled: boolean;
  registry: SlashCommand[];
  /** Role-specific idle prompt. Each app passes its own agent name so the
   *  shared shell doesn't leak "outreach agent" into admin/investor. */
  placeholder?: string;
};

export function InputWithSlashMenu({
  value,
  setValue,
  onSubmit,
  disabled,
  registry,
  placeholder = 'Type / for commands…',
}: Props) {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const showMenu = value.startsWith('/');
  const query = showMenu ? value.slice(1).split(/\s/)[0] ?? '' : '';
  const [activeIdx, setActiveIdx] = useState(0);

  const matches = showMenu
    ? registry.filter((c) => c.slash.slice(1).toLowerCase().startsWith(query.toLowerCase()))
    : [];

  useEffect(() => { setActiveIdx(0); }, [value]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSubmit(trimmed);
    setValue('');
  };

  // Choosing a command from the menu: zero-arg commands RUN immediately;
  // arg-taking commands fill the input + focus so the founder types the arg.
  const choose = (cmd: SlashCommand) => {
    if (disabled) return;
    if (ARG_REQUIRED.has(cmd.slash)) {
      setValue(`${cmd.slash} `);
      inputRef.current?.focus();
    } else {
      onSubmit(cmd.slash);
      setValue('');
    }
  };

  // True once the founder has typed a space after the command token — i.e.
  // they've started typing arguments (e.g. "/kyc inv_demo_alice"). In that
  // case Enter must SUBMIT THE FULL TEXT (args and all), not run the bare
  // highlighted command — otherwise an arg-taking slash like /kyc or /seed
  // silently loses its argument. The menu stays a discovery aid only while
  // the command token itself is being typed.
  const hasTypedArgs = showMenu && /\s/.test(value.trim());

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // When the menu is open AND no args have been typed yet, Enter RUNS the
    // highlighted command, arrows move the highlight, Tab fills, Esc clears.
    if (showMenu && matches.length > 0 && !hasTypedArgs) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, matches.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); return; }
      if (e.key === 'Tab')       { e.preventDefault(); const pick = matches[activeIdx]; if (pick) { setValue(`${pick.slash} `); inputRef.current?.focus(); } return; }
      if (e.key === 'Escape')    { e.preventDefault(); setValue(''); return; }
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); const pick = matches[activeIdx]; if (pick) choose(pick); return; }
    }
    // No menu (plain text or already-complete command): Enter submits.
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); return; }
  };

  return (
    // `relative` anchors the floating menu; `shrink-0` pins this region so the
    // textarea stays a fixed 3-row height and the menu never resizes the layout.
    <div className="relative shrink-0 border-t border-border px-3 pt-2 pb-3">
      {showMenu && matches.length > 0 && !hasTypedArgs && (
        // Absolutely positioned ABOVE the textarea — opening it floats over the
        // chat stream instead of growing the input region.
        // `w-80 max-w-[calc(100%-1.5rem)]` constrains the popup to a normal
        // autocomplete-dropdown width (320px) anchored to the textarea's left
        // edge, instead of `left-3 right-3` which stretched it to the full
        // chat-column width (banner-shaped on wide screens).
        <div
          role="listbox"
          aria-label="Slash commands"
          className="absolute bottom-full left-3 mb-1 z-50 w-80 max-w-[calc(100%-1.5rem)] rounded-md border border-border bg-background shadow-lg"
        >
          <ul className="max-h-48 overflow-y-auto px-2 py-1">
            {matches.map((cmd, i) => (
              <li key={cmd.name}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === activeIdx}
                  // onMouseDown + preventDefault: select WITHOUT blurring the
                  // textarea, so the caret/focus never leaves the input.
                  onMouseDown={(e) => { e.preventDefault(); choose(cmd); }}
                  data-active={i === activeIdx ? 'true' : undefined}
                  className="flex w-full items-center justify-between gap-3 px-2 py-1 text-sm rounded cursor-pointer text-left data-[active=true]:bg-muted hover:bg-muted"
                >
                  <span className="font-mono">{cmd.slash}</span>
                  {/* First sentence only — tool descriptions carry an
                      "Input: { … }" schema tail that's noise in a menu. */}
                  <span className="text-foreground-mute text-xs truncate">{cmd.description.split('. ')[0]}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <textarea
        ref={inputRef}
        data-testid="dock-input"
        rows={3}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        placeholder={disabled ? 'Streaming…' : placeholder}
        className="block w-full h-[4.5rem] px-3 py-2 bg-background border border-border rounded-md focus:outline-none text-sm resize-none"
      />
    </div>
  );
}
